<template>
  <div class="occ-msg">
    <div v-if="loading" class="text-center py-3">
      <b-spinner small variant="primary" />
    </div>

    <div v-else-if="error" class="text-danger small py-2">
      {{ error }}
      <a href="#" class="ml-50" @click.prevent="load">Tentar novamente</a>
    </div>

    <div v-else>
      <div ref="listEl" class="occ-msg__lista">
        <p v-if="!messages.length" class="text-muted small mb-0 py-2">
          Nenhuma mensagem nesta ocorrência.
        </p>

        <transition-group name="occ-msg" tag="div">
          <div
            v-for="m in messages"
            :key="m.id"
            class="occ-msg__item"
            :class="{ 'occ-msg__item--restrita': m.visibility === 'role' }"
          >
            <div class="occ-msg__head">
              <strong>{{ m.user_name }}</strong>
              <span v-if="m.role_name" class="text-muted"> · {{ m.role_name }}</span>
              <span class="text-muted"> · {{ originLabel(m.origin) }}</span>
              <span class="occ-msg__hora">{{ formatDate(m.created_at) }}</span>
            </div>
            <div class="occ-msg__corpo">{{ m.body }}</div>
            <span v-if="m.visibility === 'role'" class="occ-msg__tag">restrita</span>
          </div>
        </transition-group>
      </div>

      <div class="occ-msg__envio">
        <textarea
          v-model="draft"
          rows="2"
          maxlength="2000"
          class="form-control form-control-sm"
          placeholder="Escreva uma mensagem…"
        />

        <div class="d-flex align-items-center mt-1" style="gap: 12px">
          <div v-if="canChoosePrivacy" class="d-flex align-items-center" style="gap: 12px">
            <label class="mb-0 small">
              <input v-model="visibility" type="radio" value="public"> Pública
            </label>
            <label class="mb-0 small">
              <input v-model="visibility" type="radio" value="role"> Mesmo papel
            </label>
          </div>
          <span v-else class="text-muted small">Mensagem pública</span>

          <button
            type="button"
            class="btn btn-sm btn-primary ml-auto"
            :disabled="sending || !draft.trim()"
            @click="send"
          >
            <b-spinner v-if="sending" small class="mr-25" />
            Enviar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'
import axios from 'axios'

const ORIGIN_LABELS = {
  tarm: 'OCORRÊNCIAS',
  regulacao: 'REGULAÇÃO',
  despacho: 'DESPACHO',
}

export default {
  name: 'OccurrenceMessages',

  components: { BSpinner },

  props: {
    occurrenceId: { type: [Number, String], required: true },
    origin: {
      type: String,
      required: true,
      validator: v => Object.keys(ORIGIN_LABELS).includes(v),
    },
  },

  data() {
    return {
      messages: [],
      loading: false,
      error: null,
      draft: '',
      visibility: 'public',
      sending: false,
      channel: null,
      listener: null,
    }
  },

  computed: {
    canChoosePrivacy() {
      return this.origin !== 'tarm'
    },
  },

  watch: {
    occurrenceId() {
      this.unsubscribe()
      this.load()
      this.subscribe()
    },
  },

  mounted() {
    this.load()
    this.subscribe()
  },

  beforeDestroy() {
    this.unsubscribe()
  },

  methods: {
    originLabel(origin) {
      return ORIGIN_LABELS[origin] || origin
    },

    formatDate(value) {
      if (!value) return ''
      return new Date(value).toLocaleString('pt-BR')
    },

    async load({ silent = false } = {}) {
      if (!this.occurrenceId) return
      if (!silent) {
        this.loading = true
        this.error = null
      }
      try {
        const res = await axios.get(
          `${this.$store.state.api}occurrences/${this.occurrenceId}/mensagens`,
        )
        const follow = this.isAtBottom()
        this.messages = res.data.data || []
        this.error = null
        this.$emit('unread', res.data.unread_count || 0)
        if (follow) this.$nextTick(this.scrollToBottom)
      } catch (e) {
        if (silent) return
        this.messages = []
        this.error = e.response && e.response.status
          ? `Não foi possível carregar as mensagens (HTTP ${e.response.status}).`
          : 'Não foi possível carregar as mensagens.'
      } finally {
        this.loading = false
      }
    },

    async send() {
      const text = this.draft.trim()
      if (!text) return

      this.sending = true
      try {
        const res = await axios.post(
          `${this.$store.state.api}occurrences/${this.occurrenceId}/mensagens`,
          { body: text, visibility: this.visibility, origin: this.origin },
        )
        this.messages = res.data.data || this.messages
        this.draft = ''
        this.visibility = 'public'
        this.$nextTick(this.scrollToBottom)
      } catch (e) {
        this.$message('Erro', 'Não foi possível enviar a mensagem.', 'error')
      } finally {
        this.sending = false
      }
    },

    isAtBottom() {
      const el = this.$refs.listEl
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < 48
    },

    scrollToBottom() {
      const el = this.$refs.listEl
      if (el) el.scrollTop = el.scrollHeight
    },

    subscribe() {
      if (typeof window.Echo === 'undefined' || !this.occurrenceId) return

      this.channel = `occurrence.${this.occurrenceId}`
      this.listener = () => this.load({ silent: true })
      window.Echo.private(this.channel).listen('.OccurrenceMessagePosted', this.listener)
    },

    unsubscribe() {
      if (typeof window.Echo === 'undefined' || !this.channel) return

      window.Echo.private(this.channel).stopListening('.OccurrenceMessagePosted', this.listener)
      this.channel = null
      this.listener = null
    },
  },
}
</script>

<style scoped>
.occ-msg__lista {
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.occ-msg__item {
  padding: 8px 10px;
  border-bottom: 1px solid #ebe9f1;
  position: relative;
}

.occ-msg__item:last-child { border-bottom: none; }

.occ-msg__item--restrita {
  border-left: 3px solid #ff9f43;
  background: rgba(255, 159, 67, 0.06);
}

.occ-msg__head { font-size: 12px; }
.occ-msg__hora { float: right; color: #b9b9c3; font-size: 11px; }
.occ-msg__corpo { font-size: 13px; white-space: pre-wrap; margin-top: 2px; }

.occ-msg__tag {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #ff9f43;
}

.occ-msg-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.occ-msg-enter {
  opacity: 0;
  transform: translateY(8px);
}

.occ-msg-move {
  transition: transform 0.35s ease;
}

.occ-msg__envio {
  border-top: 1px solid #ebe9f1;
  padding-top: 8px;
  margin-top: 4px;
}

.dark-layout .occ-msg__item { border-bottom-color: #3b4253; }
.dark-layout .occ-msg__envio { border-top-color: #3b4253; }
</style>
